import React, {
  useCallback, useEffect, useId, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

export const IDP_CLASS_OPTIONS = [
  { value: 'nota_fiscal', labelKey: 'idpPage.form.classOptions.notaFiscal' },
  { value: 'recibo', labelKey: 'idpPage.form.classOptions.recibo' },
  { value: 'contrato', labelKey: 'idpPage.form.classOptions.contrato' },
];

function IdpClassesMultiselect({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const triggerId = useId();
  const menuId = `${triggerId}-menu`;
  const [open, setOpen] = useState(false);

  const options = IDP_CLASS_OPTIONS.map((opt) => ({
    ...opt,
    label: t(opt.labelKey),
  }));

  const selectedLabels = options
    .filter((opt) => value.includes(opt.value))
    .map((opt) => opt.label);

  const triggerLabel = selectedLabels.length > 0
    ? selectedLabels.join(', ')
    : t('idpPage.form.classOptions.placeholder');

  const setOpenState = useCallback((next) => {
    setOpen(next);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setOpenState(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpenState(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpenState]);

  const toggleOption = (optionValue) => {
    if (disabled) return;
    const isSelected = value.includes(optionValue);
    if (isSelected) {
      const next = value.filter((v) => v !== optionValue);
      onChange(next.length > 0 ? next : [optionValue]);
      return;
    }
    onChange([...value, optionValue]);
  };

  return (
    <div
      ref={rootRef}
      className={`idp-classes-multiselect${open ? ' is-open' : ''}`}
    >
      <button
        id={triggerId}
        type="button"
        className="idp-classes-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={disabled}
        onClick={() => setOpenState(!open)}
      >
        <span className="idp-classes-trigger__label">{triggerLabel}</span>
        <span className="material-symbols-rounded idp-classes-trigger__chevron" aria-hidden>
          expand_more
        </span>
      </button>
      <ul
        id={menuId}
        className="idp-classes-menu"
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby={triggerId}
        hidden={!open}
      >
        {options.map((opt) => {
          const checked = value.includes(opt.value);
          return (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="option"
                aria-selected={checked}
                className={`idp-classes-option${checked ? ' is-selected' : ''}`}
                onClick={() => toggleOption(opt.value)}
              >
                <span>{opt.label}</span>
                {checked ? (
                  <span className="material-symbols-rounded idp-classes-option__check" aria-hidden>
                    check
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

IdpClassesMultiselect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

IdpClassesMultiselect.defaultProps = {
  disabled: false,
};

export default IdpClassesMultiselect;
